import { Attention, Badge, Spinner, Typography } from "@optimizely/axiom";
import styles from "./OrganizationPage.module.scss";
import { useUserContext } from "../../../../providers/UserProvider";
import { useOrganizationDeveloperSettings } from "../../../../hooks/useOrganizationDeveloperSettings/useOrganizationDeveloperSettings";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag/useFeatureFlag";
import { Flags } from "../../../../feature-flags/flags";
import { NEW_SUPPORT_TICKET_HREF } from "../../../../constants/constants";

const SupportLink = () => (
    <a href={NEW_SUPPORT_TICKET_HREF} target="_blank" rel="noopener noreferrer">
        Optimizely Support
    </a>
);

export const OrganizationPage = () => {
    const { organizationId } = useUserContext();
    const { developerSettings: developer, loading, error } = useOrganizationDeveloperSettings({ organizationId });
    const { enabled: developerSettingsEnabled } = useFeatureFlag(Flags.ENABLE_DEVELOPER_SETTINGS);

    // Defensive: the route is already gated by this flag, but never render the section when the flag
    // is off (mirrors the support-side DeveloperSettings page).
    if (!developerSettingsEnabled) {
        return null;
    }

    // Treat a not-yet-resolved org id as "loading": UserProvider initializes organizationId to "",
    // in which case the hook makes no request (no error, no data) and we must not fall through to the
    // error state below before a request has actually been attempted.
    if (loading || !organizationId) {
        return (
            <div className="push-double content-container">
                <Spinner />
            </div>
        );
    }

    // Distinguish "failed to load" from a genuine INACTIVE status: defaulting to INACTIVE here would
    // misrepresent the organization's real developer-access state when the request errored.
    if (error || !developer) {
        return (
            <div className="push-double content-container">
                <Attention type="bad-news" data-testid="developer-access-error">
                    We couldn&apos;t load your organization&apos;s developer access status. Please try again or contact{" "}
                    <SupportLink /> if the problem persists.
                </Attention>
            </div>
        );
    }

    return (
        <div className="push-double content-container">
            <Typography type="body" className="push-double--bottom">
                This setting allows approved external developers to access OCP and build apps under your organization's
                vendor identity.
            </Typography>

            <div className={styles["organization__card"]} data-testid="developer-access-status-card">
                <div className={styles["organization__status"]}>
                    <Typography tag="h2" type="header4">
                        Developer Access Status
                    </Typography>
                    <Badge data-testid="developer-access-status-badge" color={developer.enabled ? "live" : "default"}>
                        {developer.enabled ? "ACTIVE" : "INACTIVE"}
                    </Badge>
                </div>

                {developer.enabled ? (
                    <>
                        <div className={styles["organization__vendor"]}>
                            <Typography type="body" className="weight--bold">
                                Vendor name:
                            </Typography>
                            <Typography type="body" data-testid="developer-access-vendor-name">
                                {developer.vendorName}
                            </Typography>
                        </div>
                        <Typography type="body" className="push--top">
                            If this configuration is incorrect or needs to be updated, please contact your{" "}
                            <SupportLink /> representative.
                        </Typography>
                    </>
                ) : (
                    <Typography type="body" className="push--top">
                        Developer access is not enabled for your organization. Contact <SupportLink /> to request
                        access.
                    </Typography>
                )}
            </div>

            {developer.enabled && (
                <Attention type="brand" className="push-triple--top" data-testid="developer-access-instructions">
                    <div className="weight--bold push--bottom">To grant developer access:</div>
                    <ol className={styles["organization__steps"]}>
                        <li>Go to User Management</li>
                        <li>Invite the developer user</li>
                        <li>Assign the OCP Developer role</li>
                        <li>Send the invitation</li>
                    </ol>
                </Attention>
            )}
        </div>
    );
};

OrganizationPage.displayName = "OrganizationPage";
